import { apiPost } from './api';

const IDLE_MS = 5 * 60 * 1000;
const MIN_SEGMENT_MS = 1000;
const FLUSH_INTERVAL_MS = 30 * 1000;
const ENDPOINT = '/usage_events';
// Cap re-queued events so an extended offline/API-down period can't grow the buffer
// without bound (memory + oversized keepalive payloads). Keep the most recent.
const MAX_BUFFERED_EVENTS = 2000;

function now() { return (typeof performance !== 'undefined' ? performance.now() : Date.now()); }

export function createUsageTracker({ post = (events, sessionId, opts) =>
    apiPost(ENDPOINT, { session_id: sessionId, events }, opts) } = {}) {
  let current = null;       // { key, accumulatedMs, lastResume|null }
  let buffer = [];
  let sessionId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
  let idleTimer = null;
  let flushTimer = null;
  let started = false;

  function activeElapsed() {
    if (!current || current.lastResume == null) return 0;
    return now() - current.lastResume;
  }
  function pause() {
    if (current && current.lastResume != null) {
      current.accumulatedMs += activeElapsed();
      current.lastResume = null;
    }
  }
  function resume() {
    if (current && current.lastResume == null) current.lastResume = now();
    armIdle();
  }
  function armIdle() {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(pause, IDLE_MS);
  }
  function finalizeCurrent() {
    if (!current) return;
    pause();
    if (current.accumulatedMs >= MIN_SEGMENT_MS) {
      buffer.push({
        compartment: current.key,
        duration_ms: Math.round(current.accumulatedMs),
        occurred_at: new Date().toISOString(),
      });
    }
    current = null;
  }

  function enter(key) {
    if (!key) return;
    if (current && current.key === key) return;
    finalizeCurrent();
    current = { key, accumulatedMs: 0, lastResume: now() };
    armIdle();
  }

  // Snapshot pending active time without ending the segment (so periodic flush captures
  // ongoing dwell). Works whether the segment is running or paused: activeElapsed() is 0
  // when paused, so a paused segment still gets its accumulated time flushed here instead
  // of being stranded until the next resume/finalize (and lost on a hidden/idle pagehide).
  function finalizeCurrentSnapshot() {
    if (!current) return;
    const pending = current.accumulatedMs + activeElapsed();
    if (pending >= MIN_SEGMENT_MS) {
      buffer.push({ compartment: current.key, duration_ms: Math.round(pending), occurred_at: new Date().toISOString() });
      current.accumulatedMs = 0;
      if (current.lastResume != null) current.lastResume = now();
    }
  }

  // useKeepalive=true for unload/hidden flushes so the request (with its auth
  // header) survives the page going away. fetch keepalive replaces sendBeacon,
  // which cannot set the Authorization header our API requires.
  function flush(useKeepalive = false) {
    finalizeCurrentSnapshot();
    if (buffer.length === 0) return;
    const events = buffer;
    buffer = [];
    post(events, sessionId, { keepalive: useKeepalive })
      // apiPost resolves even on 4xx/5xx (it returns the raw Response), so a server
      // error wouldn't otherwise reject. Throw on a non-OK status so the events get
      // re-queued instead of silently dropped.
      .then((resp) => { if (resp && resp.ok === false) throw resp; })
      .catch(() => {
        buffer = events.concat(buffer);
        if (buffer.length > MAX_BUFFERED_EVENTS) buffer = buffer.slice(-MAX_BUFFERED_EVENTS);
      });
  }

  function onVisibility() { document.hidden ? (pause(), flush(true)) : resume(); }
  function onActivity() { resume(); }
  function onPageHide() { flush(true); }

  function start() {
    if (started) return;
    started = true;
    ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'].forEach(e =>
      window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    flushTimer = setInterval(() => flush(false), FLUSH_INTERVAL_MS);
  }
  function stop() {
    if (!started) return;
    started = false;
    ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'].forEach(e =>
      window.removeEventListener(e, onActivity));
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    if (flushTimer) clearInterval(flushTimer);
    if (idleTimer) clearTimeout(idleTimer);
    finalizeCurrent();
    flush(false);
  }

  return { start, stop, enter, flush, _state: () => ({ current, buffer }) };
}
